import { useEffect, useState } from 'react'

import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLogout } from 'auth'
import { Keys } from 'state-manager'

import { api } from '../../../api'
import { UsersWebPagePayload } from '../../../types'
import { useChangeStatus } from './useChangeStatus'
import { useEditUser } from './useEditUser'
import { useResetMFA } from './useResetMFA'
import { useResetPassword } from './useResetPassword'

const emptyFilter: UsersWebPagePayload = {
  active: null,
  searchTerm: null,
  page: null,
  size: null
}

export const useUsers = () => {
  const [filter, onFilterChange] = useState<UsersWebPagePayload>(emptyFilter)
  const [initialFilter, setInitialFilter] = useState<UsersWebPagePayload>(emptyFilter)
  const { data, isFetching, refetch } = useQuery({
    ...api.getListPageOptions(filter),
    placeholderData: keepPreviousData
  })
  const qc = useQueryClient()
  const { logout } = useLogout()

  useEffect(() => {
    if (filter.page === null && typeof data?.form?.page === 'number') {
      onFilterChange(data.form)
      setInitialFilter(data.form)
    }
  }, [data, filter])

  const onSaved = (isCurrentUser: boolean) => {
    refetch()
    if (isCurrentUser) {
      qc.invalidateQueries({ queryKey: Keys.BankPortal.Layout })
    }
  }

  const onStatusChanged = (forceToLogout: boolean) => {
    if (forceToLogout) {
      logout({})
    } else {
      refetch()
    }
  }

  const edit = useEditUser()
  const changeStatus = useChangeStatus(onStatusChanged)
  const resetPassword = useResetPassword()

  const { startEditing } = useResetMFA(refetch)

  const resetFilter = () => onFilterChange(initialFilter)
  const onSearch = (searchTerm: string) => onFilterChange({ ...filter, searchTerm, page: 1 })
  const onActiveChange = (active: string | null) => onFilterChange({ ...filter, active, page: 1 })
  const onPageChange = (page: number, size: number) => onFilterChange({ ...filter, page, size })

  return {
    resetFilter,
    onSaved,
    onStatusChanged,
    onFilterChange,
    data,
    isFetching,
    edit,
    changeStatus,
    resetPassword,
    filter: {
      ...filter,
      onSearch,
      onActiveChange,
      onPageChange
    },
    onResetMFA: startEditing
  }
}
